import * as _ from "lodash-es";
import {
	computed,
	nextTick,
	onMounted,
	onScopeDispose,
	onUnmounted,
	reactive,
	toValue,
	unref,
	watch,
	type Ref,
} from "vue";
import { createDebouncer } from "./debouncer";
import { getInitialValue, removeDataFromPath, useFieldData } from "./field-data";
import { useFieldErrors } from "./field-error";
import { useFieldMeta, type FieldMeta } from "./field-meta";
import { useFieldRule, type FieldRule } from "./field-rule";
import {
	useInternalFormApi,
	type ErrorsByCause,
	type GenericValues,
	type InternalFormApiId,
} from "./form-context";
import type { MaybeRef, Noop, Path, PathValue } from "./types";

export type FieldOptions<TForm, TPath extends Path<TForm>> = {
	initialValue?: PathValue<TForm, TPath>;
} & FieldRule<TForm>;

export type InferTypeFromFieldOptions<
	TForm,
	TPath extends Path<TForm>,
	TOptions extends FieldOptions<TForm, TPath>,
> = TOptions extends {
	initialValue: PathValue<TForm, TPath>;
}
	? PathValue<TForm, TPath>
	: PathValue<TForm, TPath> | undefined;

export type FieldApi<TValue> = {
	data: Ref<TValue>;
	meta: Ref<FieldMeta<TValue>>;
	errors: Ref<string[] | undefined>;
	errorMap: Ref<ErrorsByCause>;
	validate: () => Promise<boolean>;
	clear: () => void;
	handleBlur: Noop;
	handleChange: (val: any) => void;
	attrs: {
		modelValue: TValue;
		errors: string[] | undefined;
		onBlur: Noop;
		"onUpdate:modelValue": (val: any) => void;
		"aria-invalid": boolean;
	};
};

export type FieldFactory<TForm> = <
	TPath extends Path<TForm>,
	TOptions extends FieldOptions<TForm, TPath>,
>(
	path: TPath,
	options?: MaybeRef<TOptions>
) => FieldApi<InferTypeFromFieldOptions<TForm, TPath, TOptions>>;

export type FieldNestedFactory<TValues> = <TPath extends Path<TValues>>(
	basepath: TPath
) => FieldFactory<PathValue<TValues, TPath>>;

export function useFieldFactory<TValues extends GenericValues>(
	id: InternalFormApiId<TValues>
): FieldFactory<TValues> {
	return (path, options) => _useField(id, path, options);
}

export function useFieldNestedFactory<TValues extends GenericValues>(
	id: InternalFormApiId<TValues>
): FieldNestedFactory<TValues> {
	return (basepath) => (path, options) =>
		_useField(id, `${basepath}.${path}` as any, options as any) as any;
}

type ValidationStep = "blur" | "change" | "all";

function _useField<TForm extends GenericValues, TPath extends Path<TForm>>(
	id: InternalFormApiId<TForm>,
	path: TPath,
	options?: MaybeRef<FieldOptions<TForm, TPath>>
): FieldApi<PathValue<TForm, TPath>> {
	const form = useInternalFormApi(id);
	const {
		options: formOptions,
		state: { values: formValues },
		validator,
		register,
	} = form;

	const meta = useFieldMeta(id, path);

	const initialValue = unref(options)?.initialValue;
	if (initialValue != null) {
		meta.value.initialValue = initialValue;
	}

	const errors = useFieldErrors(id, path);
	const schema = useFieldRule(id, options);
	const data = useFieldData(id, path);

	const errorsList = computed(() => {
		const list: string[] = [];

		for (const key in errors.value) {
			if (errors.value[key] != null) {
				list.push(errors.value[key]);
			}
		}

		return list;
	});

	const errorCount = computed(() => {
		return errorsList.value.length;
	});

	function isStep(step: ValidationStep, wanted: ValidationStep) {
		return step === "all" || step === wanted;
	}

	function syncValidation(step: ValidationStep): ErrorsByCause {
		const errors: ErrorsByCause = {};
		if (isStep(step, "change") && schema.value?.onChange != null) {
			errors.onChange = validator.validate(path, schema.value.onChange);
		}

		if (isStep(step, "blur") && schema.value?.onBlur != null) {
			errors.onBlur = validator.validate(path, schema.value.onBlur);
		}

		return errors;
	}

	const asyncChange = createDebouncer(async () => {
		if (schema.value?.onChangeAsync != null) {
			return await validator.validateAsync(path, schema.value.onChangeAsync);
		}
		return null;
	});

	const asyncBlur = createDebouncer(async () => {
		if (schema.value?.onBlurAsync != null) {
			return await validator.validateAsync(path, schema.value.onBlurAsync);
		}
		return null;
	});

	async function asyncValidation(step: ValidationStep): Promise<ErrorsByCause> {
		const errors: ErrorsByCause = {};
		if (isStep(step, "change") && schema.value?.onChangeAsync != null) {
			errors.onChange = await asyncChange(schema.value.asyncDebounceMs || 0);
		}

		if (isStep(step, "blur") && schema.value?.onBlurAsync != null) {
			errors.onBlur = await asyncBlur(schema.value.asyncDebounceMs || 0);
		}

		return errors;
	}

	async function validate(step: ValidationStep = "all") {
		// Don't validate onSubmit mode before the first submit.
		if (unref(form.options).mode === "onSubmit" && !form.submittedOnce.value) return true;

		// Only validate is field is dirty or touched
		if (!meta.value.dirty && !meta.value.touched) return true;

		const syncErrors = syncValidation(step);

		const hasSyncErrors = !!syncErrors.onBlur || !!syncErrors.onChange;

		let asyncErrors: Partial<typeof syncErrors> = {};
		if (!hasSyncErrors) {
			asyncErrors = await asyncValidation(step);
		}

		const onBlurError = syncErrors.onBlur ?? asyncErrors.onBlur ?? null;
		const onChangeError = syncErrors.onChange ?? asyncErrors.onChange ?? null;

		if (step === "all" || step === "blur") {
			errors.value = {
				...errors.value,
				onBlur: onBlurError,
			};
		}

		if (step === "all" || step === "change") {
			errors.value = {
				...errors.value,
				onChange: onChangeError,
			};
		}

		meta.value = {
			...meta.value,
			validated: true,
			valid: errorCount.value === 0,
		};

		return errorCount.value === 0;
	}

	watch(schema, () => {
		validate();
	});
	watch(
		data,
		() => {
			const isDirty = !_.isEqual(data.value, meta.value.initialValue);
			if (isDirty) {
				meta.value.dirty = isDirty;
			}

			validate("change");
		},
		{ deep: true }
	);

	if (toValue(formOptions).mode === "immediate") {
		onMounted(() => {
			meta.value.touched = true;
			validate();
		});
		watch(meta, () => {
			if (!meta.value.touched) {
				meta.value.touched = true;
			}
		});
	}

	onUnmounted(async () => {
		await nextTick();

		if (meta.value.observers <= 0) {
			errors.value = {};

			removeDataFromPath(formValues, path);
		}
	});

	async function handleBlur() {
		meta.value.touched = true;

		await validate("blur");
	}

	function handleChange(val: PathValue<TForm, TPath>) {
		data.value = val;
	}

	const field: FieldApi<PathValue<TForm, TPath>> = {
		data: data as Ref<PathValue<TForm, TPath>>,
		meta,
		errorMap: errors,
		errors: errorsList,
		clear: () => {
			data.value = getInitialValue(meta) as any;
			meta.value = {
				...meta.value,
				touched: false,
				valid: true,
				validated: false,
				dirty: false,
			};
		},
		validate,
		handleBlur,
		handleChange,
		attrs: reactive({
			modelValue: data as Ref<PathValue<TForm, TPath>>,
			errors: errorsList,
			onBlur: handleBlur,
			"onUpdate:modelValue": handleChange,
			"aria-invalid": errorCount.value > 0,
		}),
	};

	const unregister = register(field);
	onScopeDispose(unregister);

	return field;
}
